import React from 'react';
import { components as SelectComponents } from 'react-select';
import SelectList from '../SelectComponent/selectList/select-list.jsx';
import { GENERAL } from '../../assets/images/images.js';
import {
  CONFIG_WORK_CHECKLIST_ICONS_LIST,
} from '../../variables/workChecklists.js';
import './workChecklists.style.scss';

const DEFAULT_GROUP_OPTION = {
  value: 'ALL',
  label: 'Todas as verificações',
};

const DEFAULT_STATUS_BACKGROUND = '#ffffff';

const isCssColor = (value) => typeof value === 'string'
  && /^(#|rgb|hsl|var\(|linear-gradient|radial-gradient)/i.test(value.trim());

const ChecklistDropdownIndicator = (props) => (
  <SelectComponents.DropdownIndicator {...props}>
    <img
      src={GENERAL.iconSmallArrowDown}
      alt="icon-select"
      style={{ width: '24px', height: '24px' }}
    />
  </SelectComponents.DropdownIndicator>
);

class WorkChecklists extends React.Component {
  constructor(props) {
    super(props);
    this.rootRef = React.createRef();
    this.checklistRefs = {};
    this.bodyRefs = {};
    this.contentRefs = {};
    this.contentInnerRefs = {};
    this.stickyShellRefs = {};
    this.headerRefs = {};
    this.scrollContainer = null;
    this.windowScrollListening = false;
    this.resizeListening = false;
    this.stickyRaf = null;
    this.stickySnapshotByChecklist = {};
    this.stickyDebugSnapshotByChecklist = {};
    this.groupMenuScrollOriginByChecklist = {};
    this.expandedChecklistHeightByChecklist = {};
    this.checklistSizeMeasurementByChecklist = {};
    this.checklistNeverNeedsStickyByChecklist = {};
    this.stickyTransitionLockTimersByChecklist = {};
    this.contentResizeObserver = null;
    this.observedContentInnerByChecklist = {};
    this.pendingContentSyncByChecklist = {};
    this.contentSyncRafId = null;
    this.state = {
      checklistState: this.buildChecklistState(props.checklists),
      stickyChecklistState: {},
      openGroupMenuByChecklist: {},
      contentHeightByChecklist: {},
    };
  }

  componentDidMount() {
    this.setupContentResizeObserver();
    this.bindStickyListeners();
    this.updateStickyHeaders();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.checklists !== this.props.checklists) {
      this.expandedChecklistHeightByChecklist = {};
      this.checklistSizeMeasurementByChecklist = {};
      this.checklistNeverNeedsStickyByChecklist = {};
      this.setState({
        checklistState: this.buildChecklistState(this.props.checklists, this.state.checklistState, prevProps.checklists),
        stickyChecklistState: {},
        openGroupMenuByChecklist: {},
        contentHeightByChecklist: {},
      });
      this.groupMenuScrollOriginByChecklist = {};

      if (this.contentResizeObserver) {
        this.contentResizeObserver.disconnect();
      }
      this.observedContentInnerByChecklist = {};
    }

    const checklists = Array.isArray(this.props.checklists) ? this.props.checklists : [];
    checklists.forEach((_, checklistIndex) => {
      if (!this.getChecklistState(checklistIndex).isOpen) {
        return;
      }

      this.observeChecklistContentInner(checklistIndex);
      this.syncChecklistContentHeight(checklistIndex);
    });

    this.bindStickyListeners();
    this.scheduleStickyUpdate();
  }

  componentWillUnmount() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleStickyScroll);
    }

    if (this.windowScrollListening) {
      window.removeEventListener('scroll', this.handleStickyScroll);
    }

    if (this.resizeListening) {
      window.removeEventListener('resize', this.handleStickyScroll);
    }

    if (this.stickyRaf) {
      window.cancelAnimationFrame(this.stickyRaf);
    }

    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }
    if (this.contentSyncRafId) {
      window.cancelAnimationFrame(this.contentSyncRafId);
      this.contentSyncRafId = null;
    }
    this.pendingContentSyncByChecklist = {};
    this.observedContentInnerByChecklist = {};

    Object.values(this.stickyTransitionLockTimersByChecklist).forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    this.stickyTransitionLockTimersByChecklist = {};
  }

  setupContentResizeObserver = () => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.contentResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const checklistIndexValue = entry?.target?.dataset?.checklistIndex;
        if (checklistIndexValue == null) {
          return;
        }

        const checklistIndex = Number(checklistIndexValue);
        this.pendingContentSyncByChecklist[checklistIndex] = true;
      });

      if (this.contentSyncRafId) {
        return;
      }

      this.contentSyncRafId = window.requestAnimationFrame(() => {
        this.contentSyncRafId = null;
        const pendingChecklistIndexes = Object.keys(this.pendingContentSyncByChecklist);
        this.pendingContentSyncByChecklist = {};
        pendingChecklistIndexes.forEach((checklistIndex) => {
          this.syncChecklistContentHeight(Number(checklistIndex));
        });
      });
    });
  }

  observeChecklistContentInner = (checklistIndex) => {
    if (!this.contentResizeObserver) {
      return;
    }

    const contentInnerNode = this.contentInnerRefs[checklistIndex];
    if (!contentInnerNode) {
      return;
    }

    const observedNode = this.observedContentInnerByChecklist[checklistIndex];
    if (observedNode === contentInnerNode) {
      return;
    }

    if (observedNode) {
      this.contentResizeObserver.unobserve(observedNode);
    }

    this.contentResizeObserver.observe(contentInnerNode);
    this.observedContentInnerByChecklist[checklistIndex] = contentInnerNode;
  }

  syncChecklistContentHeight = (checklistIndex) => {
    const contentInnerNode = this.contentInnerRefs[checklistIndex];
    if (!contentInnerNode) {
      return;
    }

    const nextHeight = Math.ceil(contentInnerNode.scrollHeight || 0);

    this.setState((prevState) => {
      const currentHeight = prevState.contentHeightByChecklist[checklistIndex] || 0;
      if (currentHeight === nextHeight) {
        return null;
      }

      return {
        contentHeightByChecklist: {
          ...prevState.contentHeightByChecklist,
          [checklistIndex]: nextHeight,
        },
      };
    }, () => {
      this.clearMeasuredChecklistExpandedHeight(checklistIndex);
      delete this.checklistNeverNeedsStickyByChecklist[checklistIndex];
      this.scheduleStickyUpdate();
    });
  }

  isMobileViewport = () => window.matchMedia('(max-width: 992px)').matches

  getScrollContainer = () => {
    let currentElement = this.rootRef.current;

    while (currentElement && currentElement.parentElement) {
      currentElement = currentElement.parentElement;
      const computedStyle = window.getComputedStyle(currentElement);
      const overflowY = computedStyle.overflowY || computedStyle.overflow;
      const isScrollable = currentElement.scrollHeight > currentElement.clientHeight;

      if (/(auto|scroll|overlay)/.test(overflowY) && isScrollable) {
        return currentElement;
      }
    }

    return window;
  }

  bindStickyListeners = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const nextScrollContainer = this.getScrollContainer();
    if (this.scrollContainer !== nextScrollContainer) {
      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.handleStickyScroll);
      }

      this.scrollContainer = nextScrollContainer;
      if (this.scrollContainer !== window) {
        this.scrollContainer.addEventListener('scroll', this.handleStickyScroll);
      }

      if (!this.windowScrollListening) {
        window.addEventListener('scroll', this.handleStickyScroll, { passive: true });
        this.windowScrollListening = true;
      }
    }

    if (!this.resizeListening) {
      window.addEventListener('resize', this.handleStickyScroll);
      this.resizeListening = true;
    }
  }

  handleStickyScroll = () => {
    this.closeMenusOnScrollThreshold();
    this.scheduleStickyUpdate();
  }

  getCurrentScrollPosition = () => {
    if (typeof window === 'undefined') {
      return {
        windowScrollTop: 0,
        containerScrollTop: 0,
      };
    }

    return {
      windowScrollTop: window.scrollY || window.pageYOffset || 0,
      containerScrollTop: this.scrollContainer && this.scrollContainer !== window
        ? this.scrollContainer.scrollTop
        : 0,
    };
  }

  closeMenusOnScrollThreshold = () => {
    const currentScrollPosition = this.getCurrentScrollPosition();

    this.setState((prevState) => {
      const openChecklistIndexes = Object.keys(prevState.openGroupMenuByChecklist)
        .filter((checklistIndex) => prevState.openGroupMenuByChecklist[checklistIndex]);

      if (openChecklistIndexes.length === 0) {
        return null;
      }

      let nextOpenGroupMenuByChecklist = null;

      openChecklistIndexes.forEach((checklistIndex) => {
        const originScrollPosition = this.groupMenuScrollOriginByChecklist[checklistIndex];
        if (!originScrollPosition) {
          return;
        }

        const hasScrolledPastThreshold = Math.abs(currentScrollPosition.windowScrollTop - originScrollPosition.windowScrollTop) > 50
          || Math.abs(currentScrollPosition.containerScrollTop - originScrollPosition.containerScrollTop) > 50;

        if (!hasScrolledPastThreshold) {
          return;
        }

        if (!nextOpenGroupMenuByChecklist) {
          nextOpenGroupMenuByChecklist = { ...prevState.openGroupMenuByChecklist };
        }

        delete nextOpenGroupMenuByChecklist[checklistIndex];
        delete this.groupMenuScrollOriginByChecklist[checklistIndex];
      });

      if (!nextOpenGroupMenuByChecklist) {
        return null;
      }

      return { openGroupMenuByChecklist: nextOpenGroupMenuByChecklist };
    });
  }

  scheduleStickyUpdate = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.stickyRaf) {
      window.cancelAnimationFrame(this.stickyRaf);
    }

    this.stickyRaf = window.requestAnimationFrame(() => {
      this.stickyRaf = null;
      this.updateStickyHeaders();
    });
  }

  resetStickyHeader = (checklistIndex) => {
    const checklistNode = this.checklistRefs[checklistIndex];
    const stickyShellNode = this.stickyShellRefs[checklistIndex];
    const wasSticky = !!(checklistNode && checklistNode.classList.contains('work-checklist--mobile-fixed'));

    this.setChecklistStickyState(checklistIndex, false);

    if (!checklistNode || !stickyShellNode) {
      return;
    }

    checklistNode.classList.remove('work-checklist--mobile-fixed');

    if (wasSticky) {
      this.lockChecklistContentTransition(checklistIndex);
    }

    delete this.stickySnapshotByChecklist[checklistIndex];
  }

  lockChecklistContentTransition = (checklistIndex) => {
    const checklistNode = this.checklistRefs[checklistIndex];
    if (!checklistNode || typeof window === 'undefined') {
      return;
    }

    checklistNode.classList.add('work-checklist--sticky-transition-lock');

    const previousTimerId = this.stickyTransitionLockTimersByChecklist[checklistIndex];
    if (previousTimerId) {
      window.clearTimeout(previousTimerId);
    }

    this.stickyTransitionLockTimersByChecklist[checklistIndex] = window.setTimeout(() => {
      const currentChecklistNode = this.checklistRefs[checklistIndex];
      if (currentChecklistNode) {
        currentChecklistNode.classList.remove('work-checklist--sticky-transition-lock');
      }
      delete this.stickyTransitionLockTimersByChecklist[checklistIndex];
    }, 260);
  }

  logStickyDebug = (checklistIndex, payload) => {
    if (process.env.NODE_ENV === 'production') {
      return;
    }

    const previousSnapshot = this.stickyDebugSnapshotByChecklist[checklistIndex];
    const nextSnapshot = JSON.stringify(payload);

    if (previousSnapshot === nextSnapshot) {
      return;
    }

    this.stickyDebugSnapshotByChecklist[checklistIndex] = nextSnapshot;
    // eslint-disable-next-line no-console
    //console.log('[WorkChecklists][sticky-debug]', payload);
  }

  logChecklistSizeDecision = (checklistIndex, payload) => {
    if (process.env.NODE_ENV === 'production') {
      return;
    }

    // eslint-disable-next-line no-console
    //console.log('[WorkChecklists][size-decision]', {
    //  checklistIndex,
    //  ...payload,
    //});
  }

  measureChecklistExpandedHeight = (checklistIndex) => {
    if (typeof window === 'undefined') {
      return null;
    }

    const checklistNode = this.checklistRefs[checklistIndex];
    const stickyShellNode = this.stickyShellRefs[checklistIndex];

    if (!checklistNode) {
      return null;
    }

    const checklistStyle = window.getComputedStyle(checklistNode);
    const checklistMarginTop = parseFloat(checklistStyle.marginTop || '0') || 0;
    const checklistMarginBottom = parseFloat(checklistStyle.marginBottom || '0') || 0;
    const contentInnerNode = checklistNode.querySelector('.work-checklist__content-inner');
    const stickyShellHeight = stickyShellNode ? stickyShellNode.offsetHeight : 0;
    const contentHeight = contentInnerNode ? contentInnerNode.scrollHeight : 0;
    const fallbackChecklistHeight = checklistNode.offsetHeight;
    const expandedChecklistHeight = (
      stickyShellHeight > 0 || contentHeight > 0
        ? stickyShellHeight + contentHeight
        : fallbackChecklistHeight
    ) + checklistMarginTop + checklistMarginBottom;

    this.checklistSizeMeasurementByChecklist[checklistIndex] = {
      checklistOffsetHeight: checklistNode.offsetHeight,
      stickyShellHeight,
      contentHeight,
      checklistMarginTop,
      checklistMarginBottom,
      expandedChecklistHeight,
    };
    this.expandedChecklistHeightByChecklist[checklistIndex] = expandedChecklistHeight;

    return expandedChecklistHeight;
  }

  getMeasuredChecklistExpandedHeight = (checklistIndex) => (
    this.expandedChecklistHeightByChecklist[checklistIndex] || this.measureChecklistExpandedHeight(checklistIndex)
  )

  clearMeasuredChecklistExpandedHeight = (checklistIndex) => {
    delete this.expandedChecklistHeightByChecklist[checklistIndex];
  }

  scrollChecklistHeaderToTop = (checklistIndex) => {
    const checklistNode = this.checklistRefs[checklistIndex];

    if (!checklistNode || typeof window === 'undefined') {
      return;
    }

    const activeScrollContainer = this.scrollContainer || this.getScrollContainer();
    if (!activeScrollContainer) {
      return;
    }

    const checklistRect = checklistNode.getBoundingClientRect();
    const checklistTop = checklistRect.top;

    if (activeScrollContainer === window) {
      if (checklistTop >= -1) {
        return;
      }

      window.scrollBy({ top: checklistTop, behavior: 'smooth' });
      return;
    }

    if (checklistTop >= -1) {
      return;
    }

    const previousScrollTop = activeScrollContainer.scrollTop;
    const nextScrollTop = Math.max(0, previousScrollTop + checklistTop);

    activeScrollContainer.scrollTo({
      top: nextScrollTop,
      behavior: 'smooth',
    });

    window.requestAnimationFrame(() => {
      const updatedScrollTop = activeScrollContainer.scrollTop;
      const didElementScroll = Math.abs(updatedScrollTop - previousScrollTop) > 1;

      if (didElementScroll) {
        return;
      }

      window.scrollBy({ top: checklistTop, behavior: 'smooth' });
    });
  }

  getVisibleChecklistHeight = () => {
    if (typeof window === 'undefined') {
      return 0;
    }

    if (this.scrollContainer === window) {
      return window.innerHeight;
    }

    return this.scrollContainer
      ? this.scrollContainer.getBoundingClientRect().height
      : window.innerHeight;
  }

  scrollChecklistHeaderToTopAfterLayout = (checklistIndex) => {
    if (typeof window === 'undefined') {
      return;
    }

    const scrollToHeader = () => {
      const headerElement = document.getElementById(`checklist-header-${checklistIndex}`);
      if (!headerElement) {
        return;
      }

      const top = headerElement.getBoundingClientRect().top + window.scrollY - 2;

      window.scrollTo({ top, behavior: 'smooth' });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToHeader);
    });
  }

  cacheChecklistStickyNeed = (checklistIndex) => {
    if (typeof this.checklistNeverNeedsStickyByChecklist[checklistIndex] === 'boolean') {
      return !this.checklistNeverNeedsStickyByChecklist[checklistIndex];
    }

    const checklist = Array.isArray(this.props.checklists) ? this.props.checklists[checklistIndex] : null;
    const measuredChecklistExpandedHeight = this.getMeasuredChecklistExpandedHeight(checklistIndex);
    if (!measuredChecklistExpandedHeight) {
      return null;
    }

    const availableChecklistHeight = this.getVisibleChecklistHeight();
    const neverNeedsSticky = measuredChecklistExpandedHeight <= availableChecklistHeight;
    const checklistSizeMeasurement = this.checklistSizeMeasurementByChecklist[checklistIndex] || {};

    this.checklistNeverNeedsStickyByChecklist[checklistIndex] = neverNeedsSticky;
    this.logChecklistSizeDecision(checklistIndex, {
      checklistId: checklist?.id || null,
      checklistTitle: checklist?.title || null,
      measuredChecklistExpandedHeight: Math.round(measuredChecklistExpandedHeight),
      availableChecklistHeight: Math.round(availableChecklistHeight),
      checklistOffsetHeight: Math.round(checklistSizeMeasurement.checklistOffsetHeight || 0),
      checklistMarginTop: Math.round(checklistSizeMeasurement.checklistMarginTop || 0),
      checklistMarginBottom: Math.round(checklistSizeMeasurement.checklistMarginBottom || 0),
      decision: neverNeedsSticky ? 'small-list-no-sticky' : 'large-list-needs-sticky',
      scrollContainerType: this.scrollContainer === window ? 'window' : 'element',
    });

    return !neverNeedsSticky;
  }

  setChecklistStickyState = (checklistIndex, isSticky) => {
    this.setState((prevState) => {
      const currentStickyState = !!prevState.stickyChecklistState[checklistIndex];

      if (currentStickyState === isSticky) {
        return null;
      }

      const stickyChecklistState = {
        ...prevState.stickyChecklistState,
        [checklistIndex]: isSticky,
      };

      if (!isSticky) {
        delete stickyChecklistState[checklistIndex];
      }

      return { stickyChecklistState };
    });
  }

  isChecklistSticky = (checklistIndex) => !!this.state.stickyChecklistState[checklistIndex]

  isGroupMenuOpen = (checklistIndex) => !!this.state.openGroupMenuByChecklist[checklistIndex]

  handleGroupMenuOpen = (checklistIndex) => {
    this.groupMenuScrollOriginByChecklist[checklistIndex] = this.getCurrentScrollPosition();

    this.setState((prevState) => ({
      openGroupMenuByChecklist: {
        ...prevState.openGroupMenuByChecklist,
        [checklistIndex]: true,
      },
    }));
  }

  handleGroupMenuClose = (checklistIndex) => {
    delete this.groupMenuScrollOriginByChecklist[checklistIndex];

    this.setState((prevState) => {
      if (!prevState.openGroupMenuByChecklist[checklistIndex]) {
        return null;
      }

      const openGroupMenuByChecklist = { ...prevState.openGroupMenuByChecklist };
      delete openGroupMenuByChecklist[checklistIndex];

      return { openGroupMenuByChecklist };
    });
  }

  updateStickyHeaders = () => {
    const checklists = Array.isArray(this.props.checklists) ? this.props.checklists : [];

    if (!this.isMobileViewport()) {
      checklists.forEach((_, checklistIndex) => this.resetStickyHeader(checklistIndex));
      return;
    }

    const rawScrollTopBoundary = this.scrollContainer === window
      ? 0
      : this.scrollContainer.getBoundingClientRect().top;
    const scrollTopBoundary = Math.max(rawScrollTopBoundary, 0);
    const scrollBottomBoundary = this.scrollContainer === window
      ? window.innerHeight
      : this.scrollContainer.getBoundingClientRect().bottom;

    checklists.forEach((_, checklistIndex) => {
      const checklistNode = this.checklistRefs[checklistIndex];
      const bodyNode = this.bodyRefs[checklistIndex];
      const stickyShellNode = this.stickyShellRefs[checklistIndex];
      const { isOpen } = this.getChecklistState(checklistIndex);

      if (!isOpen || !checklistNode || !stickyShellNode || !bodyNode) {
        this.resetStickyHeader(checklistIndex);
        return;
      }

      const checklistNeedsSticky = this.cacheChecklistStickyNeed(checklistIndex);

      if (checklistNeedsSticky === false) {
        this.resetStickyHeader(checklistIndex);
        return;
      }

      const measuredChecklistExpandedHeight = this.getMeasuredChecklistExpandedHeight(checklistIndex);

      if (!measuredChecklistExpandedHeight) {
        this.resetStickyHeader(checklistIndex);
        return;
      }

      const checklistRect = checklistNode.getBoundingClientRect();
      const bodyRect = bodyNode.getBoundingClientRect();
      const stickyShellHeight = stickyShellNode.offsetHeight;
      const stickyShellBottom = scrollTopBoundary + stickyShellHeight;
      const isCurrentlySticky = checklistNode.classList.contains('work-checklist--mobile-fixed');
      const hasReachedStickyTop = checklistRect.top <= scrollTopBoundary;
      const checklistBodyBottom = bodyRect.bottom;
      const stickyBottomDistanceOffset = 250;
      const stickyReleaseThreshold = stickyShellBottom + stickyBottomDistanceOffset;
      const stickyToggleHysteresis = 6;
      const stickyEnterThreshold = stickyReleaseThreshold + stickyToggleHysteresis;
      const stickyExitThreshold = stickyReleaseThreshold - stickyToggleHysteresis;
      const thresholdToApply = isCurrentlySticky ? stickyExitThreshold : stickyEnterThreshold;
      const shouldFixHeader = hasReachedStickyTop
        && checklistBodyBottom > thresholdToApply;
      const stickyThresholdDelta = Math.round(checklistBodyBottom - thresholdToApply);
      const shouldLogStickyDebug = shouldFixHeader !== isCurrentlySticky || Math.abs(stickyThresholdDelta) <= 60;

      if (shouldLogStickyDebug) {
        this.logStickyDebug(checklistIndex, {
          checklistIndex,
          isCurrentlySticky,
          shouldFixHeader,
          hasReachedStickyTop,
          measuredChecklistExpandedHeight: Math.round(measuredChecklistExpandedHeight),
          stickyShellHeight: Math.round(stickyShellHeight),
          checklistTop: Math.round(checklistRect.top),
          checklistBottom: Math.round(checklistRect.bottom),
          bodyBottom: Math.round(bodyRect.bottom),
          checklistBodyBottom: Math.round(checklistBodyBottom),
          stickyShellBottom: Math.round(stickyShellBottom),
          stickyReleaseThreshold: Math.round(stickyReleaseThreshold),
          stickyEnterThreshold: Math.round(stickyEnterThreshold),
          stickyExitThreshold: Math.round(stickyExitThreshold),
          thresholdToApply: Math.round(thresholdToApply),
          stickyThresholdDelta,
        });
      }

      if (!shouldFixHeader) {
        this.resetStickyHeader(checklistIndex);
        return;
      }

      this.setChecklistStickyState(checklistIndex, true);

      if (!isCurrentlySticky) {
        this.lockChecklistContentTransition(checklistIndex);
      }

      const fixedOffset = stickyShellHeight;
      const nextSnapshot = {
        shouldFixHeader,
        stickyShellTop: scrollTopBoundary,
        stickyShellLeft: Math.round(checklistRect.left),
        stickyShellWidth: Math.round(checklistRect.width),
        stickyShellHeight,
        fixedOffset,
      };
      const previousSnapshot = this.stickySnapshotByChecklist[checklistIndex];
      const snapshotChanged = JSON.stringify(previousSnapshot) !== JSON.stringify(nextSnapshot);

      if (!snapshotChanged) {
        return;
      }

      this.stickySnapshotByChecklist[checklistIndex] = nextSnapshot;

      checklistNode.classList.add('work-checklist--mobile-fixed');
    });
  }

  setChecklistRef = (checklistIndex, node) => {
    if (node) {
      this.checklistRefs[checklistIndex] = node;
      return;
    }

    delete this.checklistRefs[checklistIndex];
  }

  setBodyRef = (checklistIndex, node) => {
    if (node) {
      this.bodyRefs[checklistIndex] = node;
      return;
    }

    delete this.bodyRefs[checklistIndex];
  }

  setContentRef = (checklistIndex, node) => {
    if (node) {
      this.contentRefs[checklistIndex] = node;
      return;
    }

    delete this.contentRefs[checklistIndex];
  }

  setContentInnerRef = (checklistIndex, node) => {
    if (node) {
      this.contentInnerRefs[checklistIndex] = node;
      this.observeChecklistContentInner(checklistIndex);
      this.syncChecklistContentHeight(checklistIndex);
      return;
    }

    const observedNode = this.observedContentInnerByChecklist[checklistIndex];
    if (this.contentResizeObserver && observedNode) {
      this.contentResizeObserver.unobserve(observedNode);
    }

    delete this.observedContentInnerByChecklist[checklistIndex];
    delete this.contentInnerRefs[checklistIndex];
  }

  setStickyShellRef = (checklistIndex, node) => {
    if (node) {
      this.stickyShellRefs[checklistIndex] = node;
      return;
    }

    delete this.stickyShellRefs[checklistIndex];
  }

  setHeaderRef = (checklistIndex, node) => {
    if (node) {
      this.headerRefs[checklistIndex] = node;
      return;
    }

    delete this.headerRefs[checklistIndex];
  }

  getChecklistKey = (checklist, checklistIndex) => (
    String(checklist?.id || checklist?.title || checklistIndex)
  )

  buildChecklistState = (checklists = [], previousChecklistState = [], previousChecklists = []) => {
    const previousStateByKey = previousChecklists.reduce((accumulator, checklist, checklistIndex) => {
      const checklistKey = this.getChecklistKey(checklist, checklistIndex);
      accumulator[checklistKey] = previousChecklistState[checklistIndex];
      return accumulator;
    }, {});

    return checklists.map((checklist, checklistIndex) => {
      const checklistKey = this.getChecklistKey(checklist, checklistIndex);
      const previousState = previousStateByKey[checklistKey];

      return {
        isOpen: previousState?.isOpen || false,
        selectedGroup: DEFAULT_GROUP_OPTION,
        selectedStatusIds: [],
      };
    });
  }

  getChecklistState = (checklistIndex) => (
    this.state.checklistState[checklistIndex] || {
      isOpen: false,
      selectedGroup: DEFAULT_GROUP_OPTION,
      selectedStatusIds: [],
    }
  )

  toggleChecklist = (checklistIndex) => {
    this.setState((prevState) => ({
      checklistState: prevState.checklistState.map((checklistState, index) => (
        index === checklistIndex
          ? { ...checklistState, isOpen: !checklistState.isOpen }
          : checklistState
      )),
    }), () => {
      const { isOpen } = this.getChecklistState(checklistIndex);

      if (isOpen) {
        window.requestAnimationFrame(() => {
          this.syncChecklistContentHeight(checklistIndex);
          this.measureChecklistExpandedHeight(checklistIndex);
          this.cacheChecklistStickyNeed(checklistIndex);
        });
      } else {
        this.clearMeasuredChecklistExpandedHeight(checklistIndex);
        this.setChecklistStickyState(checklistIndex, false);
        this.handleGroupMenuClose(checklistIndex);

        this.setState((prevState) => {
          if (prevState.contentHeightByChecklist[checklistIndex] == null) {
            return null;
          }

          const contentHeightByChecklist = { ...prevState.contentHeightByChecklist };
          delete contentHeightByChecklist[checklistIndex];
          return { contentHeightByChecklist };
        });

        window.requestAnimationFrame(() => {
          this.scrollChecklistHeaderToTop(checklistIndex);
          window.setTimeout(() => {
            this.scrollChecklistHeaderToTop(checklistIndex);
          }, 260);
        });
      }

      this.scheduleStickyUpdate();
    });
  }

  toggleStatusFilter = (checklistIndex, statusId) => {
    const normalizedStatusId = String(statusId);
    this.setState((prevState) => ({
      checklistState: prevState.checklistState.map((checklistState, index) => {
        if (index !== checklistIndex) {
          return checklistState;
        }

        const selectedStatusIds = checklistState.selectedStatusIds.includes(normalizedStatusId)
          ? checklistState.selectedStatusIds.filter((currentStatusId) => currentStatusId !== normalizedStatusId)
          : checklistState.selectedStatusIds.concat(normalizedStatusId);

        return {
          ...checklistState,
          selectedStatusIds,
        };
      }),
    }), () => {
      if (this.getChecklistState(checklistIndex).isOpen) {
        this.measureChecklistExpandedHeight(checklistIndex);
      }

      this.scheduleStickyUpdate();
      this.scrollChecklistHeaderToTopAfterLayout(checklistIndex);
    });
  }

  handleGroupChange = (checklistIndex, selectedGroup) => {
    this.setState((prevState) => ({
      checklistState: prevState.checklistState.map((checklistState, index) => (
        index === checklistIndex
          ? { ...checklistState, selectedGroup: selectedGroup || DEFAULT_GROUP_OPTION }
          : checklistState
      )),
    }), () => {
      if (this.getChecklistState(checklistIndex).isOpen) {
        this.measureChecklistExpandedHeight(checklistIndex);
      }

      this.scheduleStickyUpdate();
      this.scrollChecklistHeaderToTopAfterLayout(checklistIndex);
    });
  }

  getStatusEntries = (checklist) => (
    Array.isArray(checklist?.status) ? checklist.status : []
  )

  getStatusById = (checklist, statusId) => {
    const normalizedStatusId = String(statusId);
    return this.getStatusEntries(checklist).find((status) => String(status?.id) === normalizedStatusId) || null;
  }

  getGroupOptions = (checklist) => {
    const groups = Array.isArray(checklist?.groups) ? checklist.groups : [];
    const subtitles = groups
      .map((group) => group?.subtitle)
      .filter((subtitle, index, allSubtitles) => subtitle && allSubtitles.indexOf(subtitle) === index);

    return [
      DEFAULT_GROUP_OPTION,
      ...subtitles.map((subtitle) => ({ value: subtitle, label: subtitle })),
    ];
  }

  resolveStatusColor = (colorCode) => {
    if (!colorCode) {
      return DEFAULT_STATUS_BACKGROUND;
    }

    if (isCssColor(colorCode)) {
      return colorCode;
    }

    return DEFAULT_STATUS_BACKGROUND;
  }

  resolveStatusBackgroundStyle = (colorCode) => {
    const backgroundColor = this.resolveStatusColor(colorCode);

    return {
      backgroundColor,
      borderColor: backgroundColor,
      backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0))',
    };
  }

  resolveStatusIcon = (iconCode) => {
    if (!iconCode) {
      return null;
    }

    const iconEntry = CONFIG_WORK_CHECKLIST_ICONS_LIST.find((iconConfig) => String(iconConfig?.id) === String(iconCode));
    return iconEntry?.icon || null;
  }

  getVisibleGroups = (checklist, checklistIndex) => {
    const groups = Array.isArray(checklist?.groups) ? checklist.groups : [];
    const { selectedGroup, selectedStatusIds } = this.getChecklistState(checklistIndex);

    return groups.reduce((visibleGroups, group) => {
      const shouldFilterByGroup = selectedGroup?.value && selectedGroup.value !== DEFAULT_GROUP_OPTION.value;
      if (shouldFilterByGroup && group?.subtitle !== selectedGroup.value) {
        return visibleGroups;
      }

      const items = Array.isArray(group?.items) ? group.items : [];
      const visibleItems = selectedStatusIds.length > 0
        ? items.filter((item) => selectedStatusIds.includes(String(item?.status_id)))
        : items;

      if (visibleItems.length === 0 && !group?.obs) {
        return visibleGroups;
      }

      return visibleGroups.concat({
        ...group,
        visibleItems,
      });
    }, []);
  }

  renderStatusPill = (status, options = {}) => {
    if (!status) {
      return null;
    }

    const {
      isInteractive = false,
      isActive = false,
      onClick,
      className = '',
    } = options;
    const Icon = this.resolveStatusIcon(status?.icon);
    const backgroundStyle = this.resolveStatusBackgroundStyle(status?.color);
    const flatBackgroundStyle = {
      backgroundColor: backgroundStyle.backgroundColor,
      borderColor: backgroundStyle.borderColor,
    };
    const interactiveBackgroundStyle = isActive ? flatBackgroundStyle : backgroundStyle;
    const backgroundColor = backgroundStyle.backgroundColor;
    const hasCustomColor = backgroundColor !== DEFAULT_STATUS_BACKGROUND;
    const pillClassName = [
      'work-checklist__status-pill',
      className,
      isInteractive ? 'header' : 'item',
      isInteractive && isActive ? 'active' : '',
      !hasCustomColor ? 'fallback' : '',
    ].filter(Boolean).join(' ');

    const content = (
      <>
        {Icon && (
          <span className="work-checklist__status-icon">
            <Icon />
          </span>
        )}
        <span>{status?.label || ''}</span>
      </>
    );

    if (!isInteractive) {
      return (
        <div
          className={pillClassName}
          style={flatBackgroundStyle}
        >
          {content}
        </div>
      );
    }

    return (
      <button
        type="button"
        className={pillClassName}
        style={interactiveBackgroundStyle}
        onClick={onClick}
        aria-pressed={isActive}
      >
        {content}
      </button>
    );
  }

  renderStatusGrid = (checklist, checklistIndex) => {
    const statusEntries = this.getStatusEntries(checklist);
    const { selectedStatusIds } = this.getChecklistState(checklistIndex);

    if (statusEntries.length === 0) {
      return null;
    }

    return (
      <div className="work-checklist__status-grid">
        {statusEntries.map((status, statusIndex) => {
          const statusKey = `${status?.id || status?.label || 'status'}-${statusIndex}`;

          return (
            <React.Fragment key={statusKey}>
              {this.renderStatusPill(status, {
                isInteractive: true,
                isActive: selectedStatusIds.includes(String(status?.id)),
                onClick: () => this.toggleStatusFilter(checklistIndex, status?.id),
              })}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderGroupSelect = (checklist, checklistIndex) => {
    const groups = Array.isArray(checklist?.groups) ? checklist.groups : [];
    if (groups.length === 0) {
      return null;
    }

    const portalMenuPosition = this.isChecklistSticky(checklistIndex) ? 'fixed' : 'absolute';

    return (
      <div className="work-checklist__filters">
        <SelectList
          options={this.getGroupOptions(checklist)}
          selectedOption={this.getChecklistState(checklistIndex).selectedGroup}
          onChange={(selectedOption) => this.handleGroupChange(checklistIndex, selectedOption)}
          placeholder="Todas as verificações"
          notSearchable={true}
          isClearable={false}
          fontSize="20px"
          optionFontSize={20}
          indicatorLeft={true}
          componentsOverride={{ DropdownIndicator: ChecklistDropdownIndicator }}
          valueContainerStyle={{ padding: '2px 10px 2px 0px' }}
          optionStyle={{ paddingLeft: '40px' }}
          indicatorsContainerStyle={{ paddingLeft: '10px' }}
          dropdownIndicatorStyle={{ padding: '0 8px 0 0' }}
          controlStyle={{ minHeight: '34px', maxHeight: '34px', border: '1px solid rgba(165, 173, 189, 0.7)' }}
          portal={true}
          portalMenuPosition={portalMenuPosition}
          menuIsOpen={this.isGroupMenuOpen(checklistIndex)}
          onMenuOpen={() => this.handleGroupMenuOpen(checklistIndex)}
          onMenuClose={() => this.handleGroupMenuClose(checklistIndex)}
        />
      </div>
    );
  }

  renderGroupObservation = (group) => {
    if (!group?.obs) {
      return null;
    }

    return (
      <div className="work-checklist__obs">
        <p className="work-checklist__obs-label">
          {`OBSERVAÇÕES DA CATEGORIA ${group?.subtitle || ''}`}
        </p>
        <p className="work-checklist__obs-text">{group.obs}</p>
      </div>
    );
  }

  renderItem = (checklist, group, item, itemIndex) => {
    const status = this.getStatusById(checklist, item?.status_id);
    const itemKey = `${group?.subtitle || 'group'}-${item?.description || 'item'}-${itemIndex}`;

    return (
      <div key={itemKey} className="work-checklist__item">
        <p className="work-checklist__item-title">{item?.description || ''}</p>
        {group?.subtitle && <p className="work-checklist__group-subtitle">{group.subtitle}</p>}
        {status && this.renderStatusPill(status)}
        {item?.option_value && <p className="work-checklist__item-value">{item.option_value}</p>}
      </div>
    );
  }

  renderGroup = (checklist, group, groupIndex) => (
    <div key={`${group?.subtitle || 'group'}-${groupIndex}`} className="work-checklist__group">
      {group.visibleItems.map((item, itemIndex) => this.renderItem(checklist, group, item, itemIndex))}
      {this.renderGroupObservation(group)}
    </div>
  )

  renderChecklistBody = (checklist, checklistIndex) => {
    const visibleGroups = this.getVisibleGroups(checklist, checklistIndex);
    const hasGroups = Array.isArray(checklist?.groups) && checklist.groups.length > 0;
    const isFilteredEmpty = hasGroups && visibleGroups.length === 0;

    return (
      <div className="work-checklist__body" ref={(node) => this.setBodyRef(checklistIndex, node)}>
        {hasGroups && visibleGroups.map((group, groupIndex) => this.renderGroup(checklist, group, groupIndex))}
        {isFilteredEmpty && (
          <div className="work-checklist__empty">
            <p className="work-checklist__item-title">Não existem dados a apresentar.</p>
          </div>
        )}
      </div>
    );
  }

  renderChecklist = (checklist, checklistIndex) => {
    const { isOpen } = this.getChecklistState(checklistIndex);
    const contentHeight = this.state.contentHeightByChecklist[checklistIndex] || 0;
    const contentStyle = {
      height: isOpen ? `${contentHeight}px` : '0px',
    };

    return (
      <div
        key={`${checklist?.title || 'checklist'}-${checklistIndex}`}
        className={`work-checklist row-list-container container-kss ${isOpen ? 'open' : ''}`}
        ref={(node) => this.setChecklistRef(checklistIndex, node)}
      >
        <div
          className={`work-checklist__sticky-shell ${isOpen ? 'open' : ''}`}
          id={`checklist-header-${checklistIndex}`}
          ref={(node) => this.setStickyShellRef(checklistIndex, node)}
        >
          <button
            type="button"
            className="work-checklist__header row-list-title-container pe-4 py-2 py-md-3 ps-3 ps-md-4"
            ref={(node) => this.setHeaderRef(checklistIndex, node)}
            onClick={() => this.toggleChecklist(checklistIndex)}
            aria-expanded={isOpen}
          >
            <div className="d-flex align-items-end">
              <img className="d-none d-lg-block icon me-2 work-checklist__section-icon" src={GENERAL.checklistIcon} alt="" />
              <span className="work-checklist__title">{checklist?.title || ''}</span>
            </div>
            <img
              className="work-checklist__toggle"
              src={isOpen ? GENERAL.iconSmallArrowUp : GENERAL.iconSmallArrowDown}
              alt="toggle-checklist"
            />
          </button>
          {isOpen && (
            <div className="work-checklist__body-header">
              {this.renderStatusGrid(checklist, checklistIndex)}
              {this.renderGroupSelect(checklist, checklistIndex)}
            </div>
          )}
        </div>

        <div
          className={`work-checklist__content ${isOpen ? 'open' : ''}`}
          aria-hidden={!isOpen}
          style={contentStyle}
          ref={(node) => this.setContentRef(checklistIndex, node)}
        >
          <div
            className="work-checklist__content-inner"
            ref={(node) => this.setContentInnerRef(checklistIndex, node)}
            data-checklist-index={checklistIndex}
          >
            {this.renderChecklistBody(checklist, checklistIndex)}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const checklists = Array.isArray(this.props.checklists) ? this.props.checklists : [];

    if (checklists.length === 0) {
      return null;
    }

    return <div ref={this.rootRef}>{checklists.map((checklist, checklistIndex) => this.renderChecklist(checklist, checklistIndex))}</div>;
  }
}

export default WorkChecklists;